import { getLocale } from './localeConfig';

type DateInput = Date | string | number;
type NullableDateInput = DateInput | null | undefined;

const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

// Helper to create nullable versions of formatters
const nullable =
  <T extends (value: DateInput) => string>(fn: T) =>
  (value: NullableDateInput): string | undefined =>
    value == null ? undefined : fn(value);

// ---------------------------------------------------------------------------
//  Date only
// ---------------------------------------------------------------------------

// Ex: "22/01/2026"
export const fDateNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(toDate(value));

// Ex: "22 janv. 2026"
export const fDateShort = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(toDate(value));

// Ex: "22 janvier 2026"
export const fDateLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(toDate(value));

// ---------------------------------------------------------------------------
//  Time only
// ---------------------------------------------------------------------------

// Ex: "14:30"
export const fTimeNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    hour: 'numeric',
    minute: 'numeric',
  }).format(toDate(value));

// ---------------------------------------------------------------------------
//  Date + Time combinations
// ---------------------------------------------------------------------------

// Ex: "22/01/2026 14:30"
export const fDateNumericTimeNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(toDate(value));

// Ex: "22 janv. 2026 14:30"
export const fDateShortTimeNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(toDate(value));

// Ex: "22 janvier 2026 à 14:30"
export const fDateLongTimeNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(toDate(value));

// ---------------------------------------------------------------------------
//  Partial date formats
// ---------------------------------------------------------------------------

// Ex: "22/01"
export const fDayMonthNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'numeric',
  }).format(toDate(value));

// Ex: "22 janvier"
export const fDayMonthLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'long',
  }).format(toDate(value));

// Ex: "janvier"
export const fMonthLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
  }).format(toDate(value));

// Ex: "janvier 2026"
export const fMonthYearLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
    year: 'numeric',
  }).format(toDate(value));

// Ex: "janvier 26"
export const fMonthYearShort = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
    year: '2-digit',
  }).format(toDate(value));

// Ex: "01/2026"
export const fMonthYearNumeric = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    month: 'numeric',
    year: 'numeric',
  }).format(toDate(value));

// ---------------------------------------------------------------------------
//  With weekday
// ---------------------------------------------------------------------------

// Ex: "jeu. 22 janvier"
export const fWeekdayShortDayMonthLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  }).format(toDate(value));

// Ex: "jeudi 22 janvier"
export const fWeekdayDayMonthLong = (value: DateInput) =>
  new Intl.DateTimeFormat(getLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(toDate(value));

// ---------------------------------------------------------------------------
//  Technical formats
// ---------------------------------------------------------------------------

// Ex: "2026-01-22"
export const fDateIso = (value: DateInput) => {
  const date = toDate(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ---------------------------------------------------------------------------
//  Nullable versions (return undefined for null/undefined input)
// ---------------------------------------------------------------------------

export const fDateNumericNullable = nullable(fDateNumeric);
export const fDateShortNullable = nullable(fDateShort);
export const fDateLongNullable = nullable(fDateLong);
export const fTimeNumericNullable = nullable(fTimeNumeric);
export const fDateNumericTimeNumericNullable = nullable(fDateNumericTimeNumeric);
export const fDateShortTimeNumericNullable = nullable(fDateShortTimeNumeric);
export const fDateLongTimeNumericNullable = nullable(fDateLongTimeNumeric);
export const fDayMonthNumericNullable = nullable(fDayMonthNumeric);
export const fDayMonthLongNullable = nullable(fDayMonthLong);
export const fMonthYearNumericNullable = nullable(fMonthYearNumeric);
export const fMonthLongNullable = nullable(fMonthLong);
export const fMonthYearLongNullable = nullable(fMonthYearLong);
export const fMonthYearShortNullable = nullable(fMonthYearShort);
export const fWeekdayShortDayMonthLongNullable = nullable(fWeekdayShortDayMonthLong);
export const fWeekdayDayMonthLongNullable = nullable(fWeekdayDayMonthLong);
export const fDateIsoNullable = nullable(fDateIso);
